import { Link, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './state/auth';
import { AlertesProvider, Pastille, useAlertes } from './state/alertes';
import { IndicateurReseau, Squelette } from './components/ui';
import { exigencePour, menuPour, ongletsPour } from './components/menu';
import { Filet } from './components/Filet';
import { FileHorsLigne } from './components/FileHorsLigne';

import Connexion from './pages/Connexion';
import Taches from './pages/Taches';
import Disponibilites from './pages/Disponibilites';
import Planning from './pages/Planning';
import Locations from './pages/Locations';
import NouvelleReservation from './pages/NouvelleReservation';
import Reservation from './pages/Reservation';
import EtatDesLieux from './pages/EtatDesLieux';
import Caisse from './pages/Caisse';
import Capacite from './pages/Capacite';
import Analytique from './pages/Analytique';
import Flotte from './pages/Flotte';
import Clients from './pages/Clients';
import Journal from './pages/Journal';
import Corrections from './pages/Corrections';
import Cautions from './pages/Cautions';
import Entretien from './pages/Entretien';
import Amendes from './pages/Amendes';
import Sinistres from './pages/Sinistres';
import Factures from './pages/Factures';
import Parametres from './pages/Parametres';
import Utilisateurs from './pages/Utilisateurs';
import Plus from './pages/Plus';
import Contrats from './pages/Contrats';
import Assurances from './pages/Assurances';
import Banque from './pages/Banque';
import Echeances from './pages/Echeances';

/**
 * « Chacun son rôle » se voit d'abord ici.
 *
 * Sabrina n'a pas un menu réduit : elle a un AUTRE produit — une file de tâches,
 * aucune section stratégique. Ce qu'elle ne peut pas voir est absent, pas grisé.
 *
 * Les libellés et les regroupements vivent dans components/menu.ts, partagés
 * avec le menu du telephone : une seule source, aucun risque de divergence.
 */
function Navigation() {
  const { user, deconnexion, peut, estAgent } = useAuth();
  const { alertes } = useAlertes();
  const { pathname } = useLocation();
  if (!user) return null;
  const groupes = menuPour(peut, estAgent);

  return (
    <nav className="nav">
      <div className="logo">FRIENDS<span className="accent">.</span><small>LOCATION</small></div>

      {groupes.map((g) => (
        <div key={g.titre}>
          <div className="nav-sec">{g.titre}</div>
          {g.entrees.map((e) => {
            // Une branche s'ouvre quand on est dessus ou sur l'une de ses feuilles.
            const ouverte = Boolean(e.sous?.length)
              && (pathname === e.to || e.sous!.some((x) => pathname === x.to));
            return (
              <div key={e.to}>
                <NavLink to={e.to} end={e.to === '/'}
                         className={({ isActive }) => `${isActive ? 'actif' : ''}${alertes[e.to] ? ` alerte-${alertes[e.to]!.niveau}` : ''}`}>
                  <span aria-hidden="true" style={{ width: 18, display: 'inline-block' }}>{e.icone}</span>
                  {e.label}
                  <Pastille a={alertes[e.to]} />
                </NavLink>
                {ouverte && (
                  <div className="nav-sous">
                    {e.sous!.map((x) => (
                      <NavLink key={x.to} to={x.to}
                               className={({ isActive }) => `${isActive ? 'actif' : ''}${alertes[x.to] ? ` alerte-${alertes[x.to]!.niveau}` : ''}`}>
                        {x.label}
                        <Pastille a={alertes[x.to]} />
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div className="pied">
        <div className="qui"><b>{user.nom}</b><span>{user.roleLibelle}</span></div>
        <button className="btn btn-f btn-s" onClick={deconnexion}>Se déconnecter</button>
      </div>
    </nav>
  );
}

function Onglets() {
  const { user, estAgent } = useAuth();
  const { alertes } = useAlertes();
  if (!user) return null;
  // Sur telephone, tout ce qui vit derriere « Plus » doit quand meme se signaler.
  const totalCache = Object.entries(alertes)
    .filter(([chemin]) => !ongletsPour(estAgent).some((o) => o.to === chemin))
    .reduce((a, [, x]) => a + x.nombre, 0);
  const pireCache = Object.entries(alertes)
    .filter(([chemin]) => !ongletsPour(estAgent).some((o) => o.to === chemin))
    .some(([, x]) => x.niveau === 'danger') ? 'danger' : 'attention';
  const items = ongletsPour(estAgent);
  return (
    <div className="onglets" style={{ gridTemplateColumns: `repeat(${items.length},1fr)` }}>
      {items.map((e) => (
        <NavLink key={e.to} to={e.to} end={e.to === '/'}
                 className={({ isActive }) => (isActive ? 'actif' : '')}>
          <i aria-hidden="true">{e.icone}</i>{e.label}
          <Pastille a={e.to === '/plus'
            ? (totalCache > 0 ? { nombre: totalCache, niveau: pireCache as 'danger' | 'attention', detail: `${totalCache} point(s) à regarder` } : undefined)
            : alertes[e.to]} />
        </NavLink>
      ))}
    </div>
  );
}

function Protege({ children }: { children: JSX.Element }) {
  const { user, chargement, peut, estAgent } = useAuth();
  const loc = useLocation();
  if (chargement) return <div style={{ padding: 40 }}><Squelette /></div>;
  if (!user) return <Navigate to="/connexion" state={{ from: loc }} replace />;

  /*
   * Un ecran cache dans le menu doit aussi etre refuse quand on tape son
   * adresse. Sans ce garde, l'ecran s'ouvrait, son chargement partait en 403,
   * et l'utilisateur restait devant un squelette gris, indefiniment, sans un
   * mot. Un logiciel qui ne repond pas est pire qu'un logiciel qui refuse.
   */
  const exigence = exigencePour(loc.pathname);
  const refuse = exigence
    && ((exigence.gerantsSeulement && estAgent) || (exigence.droit && !peut(exigence.droit)));

  if (refuse) {
    return (
      <div style={{ maxWidth: 520, margin: '48px auto', padding: '0 20px' }}>
        <div className="carte carte-p">
          <h2 style={{ marginBottom: 8 }}>Cet écran ne vous est pas ouvert</h2>
          <p style={{ color: 'var(--texte2)' }}>
            {exigence.gerantsSeulement || exigence.droit === 'voir.analytique'
              ? <>Les analyses et le suivi sont réservés à Reda et Hichem. Votre poste est
                 celui du comptoir : la file du jour, les disponibilités et les encaissements.</>
              : <>Cet écran demande un droit que votre compte n'a pas. Demandez-le à Hichem,
                 qui gère les comptes et les droits.</>}
          </p>
          <div className="actions" style={{ marginTop: 16 }}>
            <Link className="btn btn-p btn-l" to="/">Revenir à mes tâches</Link>
          </div>
        </div>
      </div>
    );
  }

  return children;
}

export default function App() {
  const { user, chargement, serveurInjoignable, reessayer } = useAuth();
  // Le filet est remonte a chaque changement d'ecran : sans cette cle, une fois
  // une erreur attrapee, le message restait affiche meme apres avoir clique
  // dans le menu — l'utilisateur croyait toute l'application cassee.
  const { pathname } = useLocation();

  if (chargement) return <div style={{ padding: 40, maxWidth: 520 }}><Squelette lignes={5} /></div>;

  // Session intacte, serveur muet : surtout pas l'ecran de connexion, qui ferait
  // retaper un mot de passe qui ne passera pas davantage.
  if (!user && serveurInjoignable) {
    return (
      <div style={{ maxWidth: 520, margin: '64px auto', padding: '0 20px' }}>
        <div className="carte carte-p">
          <h2 style={{ marginBottom: 8 }}>Le serveur ne répond pas</h2>
          <p style={{ color: 'var(--texte2)' }}>
            Vous êtes toujours connecté — <b>ce n'est pas la peine de vous identifier à nouveau</b>.
            Le logiciel n'arrive simplement pas à joindre le serveur pour le moment.
          </p>
          <p style={{ color: 'var(--texte2)', marginTop: 10 }}>
            Vérifiez votre connexion. Si elle fonctionne, c'est le serveur de l'agence qui est
            momentanément indisponible. <b>Aucune de vos données n'est perdue.</b>
          </p>
          <div className="actions" style={{ marginTop: 16 }}>
            <button className="btn btn-p btn-l" onClick={reessayer}>Réessayer</button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/connexion" element={<Connexion />} />
        <Route path="*" element={<Navigate to="/connexion" replace />} />
      </Routes>
    );
  }

  return (
    <AlertesProvider>
    <div className="app">
      <Navigation />
      <main className="principal">
        <IndicateurReseau />
        <FileHorsLigne />
        <Filet key={pathname}>
        <Routes>
          <Route path="/connexion" element={<Navigate to="/" replace />} />
          <Route path="/" element={<Protege><Taches /></Protege>} />
          <Route path="/disponibilites" element={<Protege><Disponibilites /></Protege>} />
          <Route path="/planning" element={<Protege><Planning /></Protege>} />
          <Route path="/locations" element={<Protege><Locations /></Protege>} />
          <Route path="/contrats" element={<Protege><Contrats /></Protege>} />
          <Route path="/reservations/nouvelle" element={<Protege><NouvelleReservation /></Protege>} />
          <Route path="/reservations/:id" element={<Protege><Reservation /></Protege>} />
          <Route path="/contrats/:id/etat-des-lieux/:sens" element={<Protege><EtatDesLieux /></Protege>} />
          <Route path="/caisse" element={<Protege><Caisse /></Protege>} />
          <Route path="/banque" element={<Protege><Banque /></Protege>} />
          <Route path="/capacite" element={<Protege><Capacite /></Protege>} />
          <Route path="/analytique" element={<Protege><Analytique /></Protege>} />
          <Route path="/flotte" element={<Protege><Flotte /></Protege>} />
          <Route path="/clients" element={<Protege><Clients /></Protege>} />
          <Route path="/corrections" element={<Protege><Corrections /></Protege>} />
          <Route path="/cautions" element={<Protege><Cautions /></Protege>} />
          <Route path="/factures" element={<Protege><Factures /></Protege>} />
          <Route path="/entretien" element={<Protege><Entretien /></Protege>} />
          <Route path="/entretien/:cle" element={<Protege><Echeances mode="entretien" /></Protege>} />
          <Route path="/controle-technique" element={<Protege><Echeances mode="controle" /></Protege>} />
          <Route path="/assurances" element={<Protege><Assurances /></Protege>} />
          <Route path="/amendes" element={<Protege><Amendes /></Protege>} />
          <Route path="/sinistres" element={<Protege><Sinistres /></Protege>} />
          <Route path="/parametres" element={<Protege><Parametres /></Protege>} />
          <Route path="/utilisateurs" element={<Protege><Utilisateurs /></Protege>} />
          <Route path="/journal" element={<Protege><Journal /></Protege>} />
          <Route path="/plus" element={<Protege><Plus /></Protege>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Filet>
      </main>
      <Onglets />
    </div>
    </AlertesProvider>
  );
}
